// Packages
import { cloneDeep, isEmpty } from "lodash";

// Utils
import {
  channelOptions,
  countryDomainOptions,
  channelByCountryDomain,
} from "utils/channel-options/const";

// Local Const
import {
  defaultStartTimeList,
  defaultChannels,
  defaultChannelWidths,
  defaultWirelessModes,
  defaultTxPowers,
} from '../const';

export const getMainSendRequestParams = ({
  auth,
  method = "read",
}) => {
  const isStandaloneMode = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const apiBaseUrl = isStandaloneMode ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
  const apiUrl = `${apiBaseUrl}/sites/:siteId/settings/radio.${method}`;

  const requestParamsConfig = {
    standalone: {
      orgId: auth.realm?.id,
      siteId: auth.sites?.id,
    },
    nonStandalone: {
      entId: auth.realm?.id,
      orgId: auth.orgs?.id,
      siteId: auth.sites?.id,
    },
  };

  const requestParams = isStandaloneMode ? requestParamsConfig["standalone"] : requestParamsConfig["nonStandalone"];

  return {
    apiUrl,
    requestParams
  };
}

export const getInitRadioData = ({
  responseData
}) => {
  return {
    ...responseData
  }
}

export const getInitNeighborAPScheduleList = ({
  radioData
}) => {
  const schedule = radioData?.["wireless.radioGlobal"]?.neighborApDetectionSchedule ?? [];
  return defaultStartTimeList.map((item) => ({
    ...item,
    isSelected: schedule.includes(item.value),
  }));
}

export const getInitAutoPowerScheduleList = ({
  radioData
}) => {
  const schedule = radioData?.["wireless.radioGlobal"]?.autoPowerSchedule ?? [];
  return defaultStartTimeList.map((item) => ({
    ...item,
    isSelected: schedule.includes(item.value),
  }));
}

const getSiteCountryBasedChannels = ({
  selectedBand,
  selectCountry
}) => {
  let countryBasedChannel = [];

  if (!selectedBand?.key || !selectCountry?.value) return countryBasedChannel;

  const countryDomain = countryDomainOptions?.[selectedBand.key]?.[selectCountry?.value];
  if (countryDomain?.domain) {
    const { indoor = [], outdoor = [] } = channelByCountryDomain?.[selectedBand.key]?.[countryDomain?.domain] || {};
    countryBasedChannel = [...new Set([...indoor, ...outdoor])].sort((a, b) => a - b);
  }

  return countryBasedChannel;
}

export const getInitChannels = ({
  channel,
  selectedBand,
  selectCountry
}) => {
  const selectedChannelSet = new Set(channel || []);
  const countryBasedChannel = getSiteCountryBasedChannels({
    selectedBand,
    selectCountry
  });

  let clonedChannels = cloneDeep(defaultChannels);
  clonedChannels[selectedBand?.value] = countryBasedChannel.map(ch => {
    return {
      id: ch,
      value: ch,
      isSelected: selectedChannelSet.has(ch)
    }
  })

  return clonedChannels;
}

export const getInitChannelWidths = ({
  channelWidth,
  selectedBand,
}) => {
  if (!channelWidth || !defaultChannelWidths[selectedBand.value]) return cloneDeep(defaultChannelWidths);

  let clonedChannelWidths = {};

  Object.keys(defaultChannelWidths).forEach(key => {
    if (key === selectedBand.value) {
      clonedChannelWidths[key] = defaultChannelWidths[key].map(chw => {
        return { ...chw, isSelected: chw.value === channelWidth }
      })
    } else {
      clonedChannelWidths[key] = cloneDeep(defaultChannelWidths[key]);
    }
  })

  return clonedChannelWidths;
}

export const getInitWirelessModes = ({
  wirelessMode,
  selectedBand,
}) => {
  if (!wirelessMode || !defaultWirelessModes[selectedBand.value]) return cloneDeep(defaultWirelessModes);

  let cloneWirelessModes = {};

  Object.keys(defaultWirelessModes).forEach(key => {
    if (key === selectedBand.value) {
      cloneWirelessModes[key] = defaultWirelessModes[key].map(mode => {
        return { ...mode, isSelected: mode.value === wirelessMode }
      })
    } else {
      cloneWirelessModes[key] = cloneDeep(defaultWirelessModes[key]);
    }
  })

  return cloneWirelessModes;
}

export const getInitTxPowers = ({
  txPower,
  selectedBand,
}) => {
  if (!txPower || !defaultTxPowers[selectedBand.value]) return cloneDeep(defaultTxPowers);

  let clonedTxPower = {};

  Object.keys(defaultTxPowers).forEach(key => {
    if (key === selectedBand.value) {
      clonedTxPower[key] = defaultTxPowers[key].map(power => {
        return { ...power, isSelected: power.value === txPower }
      })
    } else {
      clonedTxPower[key] = cloneDeep(defaultTxPowers[key]);
    }
  })

  return clonedTxPower;
}

export const getCleanPayload = ({
  radioData,
}) => {
  const clonedRadioData = cloneDeep(radioData);

  if (Array.isArray(clonedRadioData?.["wireless.radio"])) {
    clonedRadioData["wireless.radio"] = clonedRadioData["wireless.radio"].map(radio => {
      if (radio.band === "2" && String(radio.chanWidth) === "20") {
        return {
          ...radio,
          h2040Coexistence: false,
        };
      }
      return radio;
    });
  }

  const payload = {
    data: {
      ...clonedRadioData,
    }
  };

  return payload;
}

export const getIsFormInvalid = ({
  radioData,
}) => {
  let isInvalid = false;

  if (radioData?.["wireless.radioGlobal"]?.neighborApDetection) {
    if (!radioData["wireless.radioGlobal"]?.neighborApDetectionSchedule?.length) isInvalid = true;
  }

  const radioList = radioData?.["wireless.radio"] || [];
  for (const radio of radioList) {
    if (radio?.dtimInterval == null || radio?.dtimInterval === "" || radio?.dtimInterval < 1 || radio?.dtimInterval > 15) isInvalid = true;
    if (radio?.rtsLength == null || radio?.rtsLength === "" || radio?.rtsLength < 256 || radio?.rtsLength > 2346) isInvalid = true;
    if (radio?.fragmentLength == null || radio?.fragmentLength === "" || radio?.fragmentLength < 256 || radio?.fragmentLength > 2346) isInvalid = true;

    if (radio?.multicastBandwidthControl) {
      if (radio?.maxMulticastBandwidth == null || radio?.maxMulticastBandwidth === "" || radio?.maxMulticastBandwidth < 1 || radio?.maxMulticastBandwidth > 1024) isInvalid = true;
    }

    if (radio?.connectionLimit) {
      if (radio?.userLimit == null || radio?.userLimit === "" || radio?.userLimit < 0 || radio?.userLimit > 64) isInvalid = true;
      if (radio?.networkUtilization == null) isInvalid = true;
    }

    if (radio?.rssiThresholdEnable) {
      if (radio?.rssiThreshold == null) isInvalid = true;
    }
  }

  return isInvalid;
}